import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import SearchFilters from './components/SearchFilters';
import SearchResults from './components/SearchResults';
import ShortlistPanel from './components/ShortlistPanel';
import SavedSearchModal from './components/SavedSearchModal';

const EmployerSearchInterface = () => {
  const navigate = useNavigate();
  const [isFiltersCollapsed, setIsFiltersCollapsed] = useState(false);
  const [isShortlistOpen, setIsShortlistOpen] = useState(false);
  const [isSaveSearchModalOpen, setIsSaveSearchModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [shortlistedCandidates, setShortlistedCandidates] = useState([]);

  const [filters, setFilters] = useState({
    skills: [],
    location: '',
    radius: 50,
    experience: '',
    education: '',
    languages: [],
    salaryMin: 400000,
    salaryMax: 1200000,
    availability: '',
    workType: '',
    workPermit: false,
    remoteWork: false
  });

  const [sortBy, setSortBy] = useState('match');

  // Mock candidates data
  const mockCandidates = [
    {
      id: "1234",
      title: "Senior Frontend Developer",
      skills: ["React", "TypeScript", "Node.js", "AWS", "GraphQL", "Jest"],
      location: "Oslo",
      experienceLevel: "senior",
      education: "Master\'s Degree",
      languages: ["Norwegian", "English", "German"],
      availability: "immediate",
      matchPercentage: 95,
      remoteWork: true,
      workPermit: true,
      salaryRange: { min: 700000, max: 900000 }
    },
    {
      id: "5678",
      title: "Full Stack Developer",
      skills: ["JavaScript", "Python", "Django", "React", "PostgreSQL"],
      location: "Bergen",
      experienceLevel: "mid",
      education: "Bachelor\'s Degree",
      languages: ["Norwegian", "English"],
      availability: "2weeks",
      matchPercentage: 88,
      remoteWork: true,
      workPermit: true,
      salaryRange: { min: 600000, max: 750000 }
    },
    {
      id: "9012",
      title: "DevOps Engineer",
      skills: ["Docker", "Kubernetes", "AWS", "Terraform", "Python", "Linux"],
      location: "Trondheim",
      experienceLevel: "senior",
      education: "Bachelor\'s Degree",
      languages: ["Norwegian", "English", "Swedish"],
      availability: "1month",
      matchPercentage: 92,
      remoteWork: false,
      workPermit: true,
      salaryRange: { min: 750000, max: 950000 }
    },
    {
      id: "3456",
      title: "UI/UX Designer",
      skills: ["Figma", "Adobe XD", "Sketch", "Prototyping", "User Research"],
      location: "Stavanger",
      experienceLevel: "mid",
      education: "Bachelor\'s Degree",
      languages: ["Norwegian", "English", "Danish"],
      availability: "immediate",
      matchPercentage: 78,
      remoteWork: true,
      workPermit: true,
      salaryRange: { min: 550000, max: 700000 }
    },
    {
      id: "7890",
      title: "Backend Developer",
      skills: ["Java", "Spring Boot", "MySQL", "Redis", "Microservices"],
      location: "Oslo",
      experienceLevel: "lead",
      education: "Master\'s Degree",
      languages: ["Norwegian", "English", "French"],
      availability: "3months",
      matchPercentage: 85,
      remoteWork: false,
      workPermit: true,
      salaryRange: { min: 800000, max: 1100000 }
    },
    {
      id: "2468",
      title: "Data Scientist",
      skills: ["Python", "Machine Learning", "TensorFlow", "SQL", "R"],
      location: "Oslo",
      experienceLevel: "senior",
      education: "PhD",
      languages: ["Norwegian", "English", "Spanish"],
      availability: "2weeks",
      matchPercentage: 90,
      remoteWork: true,
      workPermit: true,
      salaryRange: { min: 750000, max: 950000 }
    }
  ];

  const [candidates, setCandidates] = useState(mockCandidates);
  const [totalCount, setTotalCount] = useState(mockCandidates?.length);

  useEffect(() => {
    // Simulate search with filters
    setIsLoading(true);
    setTimeout(() => {
      let filteredCandidates = [...mockCandidates];
      
      // Apply filters
      if (filters?.skills?.length > 0) {
        filteredCandidates = filteredCandidates?.filter(candidate =>
          filters?.skills?.some(skill => 
            candidate?.skills?.some(candidateSkill => 
              candidateSkill?.toLowerCase()?.includes(skill?.toLowerCase())
            )
          )
        );
      }
      
      if (filters?.location) {
        filteredCandidates = filteredCandidates?.filter(candidate =>
          candidate?.location?.toLowerCase()?.includes(filters?.location?.toLowerCase())
        );
      }
      
      if (filters?.experience) {
        filteredCandidates = filteredCandidates?.filter(candidate =>
          candidate?.experienceLevel === filters?.experience
        );
      }
      
      if (filters?.remoteWork) {
        filteredCandidates = filteredCandidates?.filter(candidate =>
          candidate?.remoteWork === true
        );
      }
      
      if (filters?.workPermit) {
        filteredCandidates = filteredCandidates?.filter(candidate =>
          candidate?.workPermit === true
        );
      }

      // Apply sorting
      switch (sortBy) {
        case 'match':
          filteredCandidates?.sort((a, b) => b?.matchPercentage - a?.matchPercentage);
          break;
        case 'experience':
          const expOrder = { 'entry': 1, 'mid': 2, 'senior': 3, 'lead': 4 };
          filteredCandidates?.sort((a, b) => expOrder?.[b?.experienceLevel] - expOrder?.[a?.experienceLevel]);
          break;
        case 'salary':
          filteredCandidates?.sort((a, b) => b?.salaryRange?.max - a?.salaryRange?.max);
          break;
        default:
          break;
      }
      
      setCandidates(filteredCandidates);
      setTotalCount(filteredCandidates?.length);
      setIsLoading(false);
    }, 800);
  }, [filters, sortBy]);

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleSortChange = (newSort) => {
    setSortBy(newSort);
  };

  const handleUnlockCandidate = (candidate) => {
    navigate('/profile-unlock-payment', { state: { candidateData: candidate } });
  };

  const handleAddToShortlist = (candidate) => {
    setShortlistedCandidates(prev => {
      const isAlreadyShortlisted = prev?.some(c => c?.id === candidate?.id);
      if (isAlreadyShortlisted) {
        return prev?.filter(c => c?.id !== candidate?.id);
      } else {
        return [...prev, { ...candidate, note: '' }];
      }
    });
  };

  const handleRemoveFromShortlist = (candidateId) => {
    setShortlistedCandidates(prev => prev?.filter(c => c?.id !== candidateId));
  };

  const handleAddNote = (candidateId, note) => {
    setShortlistedCandidates(prev =>
      prev?.map(c => c?.id === candidateId ? { ...c, note } : c)
    );
  };

  const handleSaveSearch = (searchData) => {
    // In a real app, this would save to backend
    console.log('Saving search:', searchData);
    // Show success message or redirect
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="container mx-auto px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-2 text-sm text-muted-foreground mb-2">
            <button 
              onClick={() => navigate('/employer-dashboard')}
              className="hover:text-foreground transition-colors"
            >
              Dashboard
            </button>
            <Icon name="ChevronRight" size={16} />
            <span>Search Candidates</span>
          </div>
          
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div>
              <h1 className="text-3xl font-semibold text-foreground mb-2">
                Find Your Next Hire
              </h1>
              <p className="text-muted-foreground">
                Discover talented candidates through our anonymous, skills-first platform
              </p>
            </div>
            
            <div className="flex items-center space-x-3">
              <Button
                variant="outline"
                onClick={() => setIsSaveSearchModalOpen(true)}
                iconName="Bookmark"
                iconPosition="left"
              >
                Saved Searches
              </Button>
              
              <Button
                variant="outline"
                onClick={() => setIsShortlistOpen(true)}
                iconName="Users"
                iconPosition="left"
              >
                Shortlist ({shortlistedCandidates?.length})
              </Button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Filters Sidebar */}
          <div className={`lg:col-span-1 ${isFiltersCollapsed ? 'lg:hidden' : ''}`}>
            <div className="sticky top-24">
              <SearchFilters
                filters={filters}
                onFiltersChange={handleFiltersChange}
                onSaveSearch={() => setIsSaveSearchModalOpen(true)}
                isCollapsed={isFiltersCollapsed}
                onToggleCollapse={() => setIsFiltersCollapsed(!isFiltersCollapsed)}
              />
            </div>
          </div>

          {/* Results Area */}
          <div className={`${isFiltersCollapsed ? 'lg:col-span-4' : 'lg:col-span-3'}`}>
            {/* Mobile Filter Toggle */}
            <div className="lg:hidden mb-6">
              <Button
                variant="outline"
                onClick={() => setIsFiltersCollapsed(!isFiltersCollapsed)}
                iconName="Filter"
                iconPosition="left"
                fullWidth
              >
                {isFiltersCollapsed ? 'Show' : 'Hide'} Filters
              </Button>
            </div>

            <SearchResults
              candidates={candidates}
              totalCount={totalCount}
              isLoading={isLoading}
              sortBy={sortBy}
              onSortChange={handleSortChange}
              onUnlockCandidate={handleUnlockCandidate}
              onAddToShortlist={handleAddToShortlist}
              shortlistedCandidates={shortlistedCandidates}
            />
          </div>
        </div>
      </div>
      {/* Shortlist Panel */}
      <ShortlistPanel
        shortlistedCandidates={shortlistedCandidates}
        onRemoveFromShortlist={handleRemoveFromShortlist}
        onAddNote={handleAddNote}
        isOpen={isShortlistOpen}
        onToggle={() => setIsShortlistOpen(!isShortlistOpen)}
      />
      {/* Save Search Modal */}
      <SavedSearchModal
        isOpen={isSaveSearchModalOpen}
        onClose={() => setIsSaveSearchModalOpen(false)}
        filters={filters}
        onSaveSearch={handleSaveSearch}
      />
    </div>
  );
};

export default EmployerSearchInterface;